import { defineQueries, defineQuery } from "@rocicorp/zero"
import z from "zod"
import { zql } from "./schema"

export const queries = defineQueries({
	projects: {
		// Get all projects the user is a member of (via project_members)
		all: defineQuery(z.undefined(), () => zql.projects),

		// Get a project by ID with nodes, edges, and members
		byId: defineQuery(z.string(), ({ args: projectId }) =>
			zql.projects
				.where("id", "=", projectId)
				.related("project_nodes", (query) =>
					query.related("geometries", (query) =>
						query
							.where("status", "=", "completed")
							.orderBy("created_at", "desc")
							.limit(1),
					),
				)
				.related("project_edges")
				.related("project_members", (query) => query.related("user"))
				.one(),
		),

		// Get project runtime data needed to materialize local execution files.
		runtimeById: defineQuery(z.string(), ({ args: projectId }) =>
			zql.projects
				.where("id", "=", projectId)
				.related("project_nodes")
				.related("project_edges")
				.one(),
		),

		// Canonical Electron execution snapshot. Keep this named so it is synced
		// before the local coordinator evaluates graph changes.
		executionById: defineQuery(z.string(), ({ args: projectId }) =>
			zql.projects
				.where("id", "=", projectId)
				.related("project_nodes", (query) =>
					query.related("geometries", (geometries) =>
						geometries
							.where("status", "=", "completed")
							.orderBy("created_at", "desc")
							.limit(1),
					),
				)
				.related("project_edges")
				.one(),
		),

		// Get project by share token (for public share page)
		byShareToken: defineQuery(z.string(), ({ args: shareToken }) =>
			zql.projects
				.where("share_token", "=", shareToken)
				.where("share_enabled", "=", true)
				.related("project_nodes", (query) =>
					query.related("geometries", (query) =>
						query.where("status", "=", "completed"),
					),
				)
				.related("project_edges")
				.one(),
		),
	},

	// Project membership queries
	members: {
		// Get all members of a project
		byProject: defineQuery(z.string(), ({ args: projectId }) =>
			zql.project_members.where("project_id", "=", projectId).related("user"),
		),

		// Get all project memberships for a user (to list their projects)
		byUser: defineQuery(z.string(), ({ args: userId }) =>
			zql.project_members.where("user_id", "=", userId).related("project"),
		),

		// Get a specific membership (to check user's role)
		byProjectAndUser: defineQuery(
			z.object({ projectId: z.string(), userId: z.string() }),
			({ args: { projectId, userId } }) =>
				zql.project_members
					.where("project_id", "=", projectId)
					.where("user_id", "=", userId)
					.one(),
		),
	},

	// Invitation queries
	invitations: {
		// Get all invitations for a project (for project owners)
		byProject: defineQuery(z.string(), ({ args: projectId }) =>
			zql.project_invitations
				.where("project_id", "=", projectId)
				.related("invited_by_user")
				.related("project"),
		),

		// Get pending invitations for an email (for dashboard)
		pendingByEmail: defineQuery(z.string(), ({ args: email }) =>
			zql.project_invitations
				.where("email", "=", email.toLowerCase())
				.where("status", "=", "pending")
				.related("project")
				.related("invited_by_user"),
		),

		// Get an invitation by ID
		byId: defineQuery(z.string(), ({ args: invitationId }) =>
			zql.project_invitations
				.where("id", "=", invitationId)
				.related("project")
				.related("invited_by_user")
				.one(),
		),
	},

	geometries: {
		byNodeIds: defineQuery(z.array(z.string()), ({ args: nodeIds }) =>
			zql.geometries
				.where("project_node_id", "IN", nodeIds)
				.where("status", "=", "completed")
				.orderBy("created_at", "desc"),
		),
		// byProject: defineQuery(z.string(), ({ args: projectId }) =>
		// 	zql.geometries
		// 		.where("project_id", "=", projectId)
		// 		.where("status", "=", "completed")
		// 		.orderBy("created_at", "desc"),
		// ),
		// byNode: defineQuery(z.string(), ({ args: nodeId }) =>
		// 	zql.geometries
		// 		.where("project_node_id", "=", nodeId)
		// 		.where("status", "=", "completed")
		// 		.orderBy("created_at", "desc")
		// 		.limit(1),
		// ),
	},

	projectNodes: {
		byId: defineQuery(z.string(), ({ args: nodeId }) =>
			zql.project_nodes.where("id", "=", nodeId).one(),
		),
	},
})
